import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import {
  coachMetaLine,
  sessionTypeCount,
} from "app/routes/Sessions/AllCoachesPublicSession/_components/coachMeta";
import {
  getApplicablePackageCredit,
  PackageCredit,
  rateRequiresPackage,
} from "app/utils/creditBundles";
import { cls } from "app/utils";

import styles from "./BookingCards.module.scss";
import CoachAvatar from "../AllCoachesPublicSession/_components/CoachAvatar";

interface Props {
  coaches: CoachProfileModel[];
  coach?: CoachProfileModel;
  onCoachChange: (coach: CoachProfileModel) => void;
  options: LessonRateModel[];
  option?: LessonRateModel;
  onOptionChange: (option: LessonRateModel) => void;
  coachesEnabled: boolean;
  optionsEnabled: boolean;
  timezone?: string;
  /**
   * Every package the athlete holds, so the chosen option and each row of the
   * menu can say what booking it would draw on — the frame writes that under
   * each option, not only under the chosen one.
   */
  packageCredits?: Map<number, PackageCredit>;
}

/**
 * The two cards above the calendar on the booking screen: who the session is
 * with, and which option it is. The frame puts each behind a chevron beside its
 * name rather than in a labelled select — same choices, same handlers.
 */
const BookingCards: React.FC<Props> = ({
  coaches,
  coach,
  onCoachChange,
  options,
  option,
  onOptionChange,
  coachesEnabled,
  optionsEnabled,
  timezone,
  packageCredits,
}) => {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState<"coach" | "option" | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);

  // An open menu covers the calendar underneath it, so it has to be dismissable
  // without choosing anything — clicking away or pressing Escape.
  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!cardsRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  const canSwitchCoach = coachesEnabled && coaches.length > 1;
  const canSwitchOption = optionsEnabled && options.length > 1;
  const metaLine = coach ? coachMetaLine(coach) : "";

  // Same line the coach page shows under an option: its length and, when the
  // coach set one, where it happens. Lesson rates carry no description of their
  // own (that is CN-2573), so this is built from what they do carry.
  const describe = (rate: LessonRateModel) =>
    rate.location
      ? t("{{duration}} session at {{location}}", {
          duration: rate.duration,
          location: rate.location,
        })
      : t("{{duration}} session", { duration: rate.duration });

  const optionDescription = option ? describe(option) : "";

  /**
   * What booking an option would draw on, in the coach page's three states —
   * because that is where the athlete met the option first (CN-2652 feedback):
   *
   *   held package  → ⭐ "Package: N sessions remaining"
   *   package-only  → ⟳ "Requires Subscription"
   *   otherwise     → the price
   *
   * The star carries what is left on the package rather than only the intent to
   * apply it: the athlete is choosing between options here, and how many
   * sessions each one still has on it is part of that choice.
   */
  const statusLine = (rate: LessonRateModel) => {
    const credit = getApplicablePackageCredit(rate, packageCredits);
    if (credit) {
      return (
        <div className={styles.package_note}>
          <i className="ico ico-star" />
          <span>
            {credit.unlimited
              ? t("Package: unlimited sessions")
              : t("Package: {{count}} sessions remaining", {
                  count: credit.remainingSessions,
                })}
          </span>
        </div>
      );
    }
    if (rateRequiresPackage(rate, packageCredits)) {
      return (
        <div className={styles.subscription_note}>
          <i className="ico ico-repeat" />
          <span>{t("Requires Subscription")}</span>
        </div>
      );
    }
    // CN-2711: the option is booked without its price on show. Nothing stands in
    // for it — the line is simply not there, as on every other athlete surface.
    if (rate.hidePrice) {
      return null;
    }
    return (
      <div className={styles.meta}>
        {generateCurrencySymbol(coach?.currency)}
        {rate.rate}
      </div>
    );
  };

  return (
    <div ref={cardsRef}>
      <div className={styles.card}>
        <CoachAvatar coach={coach} className={styles.avatar} fontSize={24} />
        <div className={styles.details}>
          <div className={styles.name_row}>
            <span className={styles.name}>{coach?.fullName}</span>
            {canSwitchCoach && (
              <button
                type="button"
                className={cls(styles.chevron_button, {
                  [styles.chevron_button_open]: openMenu === "coach",
                })}
                onClick={() =>
                  setOpenMenu((open) => (open === "coach" ? null : "coach"))
                }
                aria-label={t("Coach")}
                aria-expanded={openMenu === "coach"}
                data-testid="booking-coach-switch"
              >
                <i className="ico ico-chevron" />
              </button>
            )}
          </div>
          {metaLine !== "" && <div className={styles.meta}>{metaLine}</div>}
          {timezone && (
            <div className={styles.timezone}>
              <i className="ico ico-earth" />
              <span>{timezone}</span>
            </div>
          )}

          {openMenu === "coach" && (
            // Same anatomy as the option menu: the picture and the line the card
            // above shows, so switching coach reads like switching option.
            <div className={styles.menu}>
              {coaches.map((item) => {
                const itemMeta = coachMetaLine(item);
                return (
                  <button
                    key={item.id || item.calendarUuid}
                    type="button"
                    className={styles.coach_row}
                    onClick={() => {
                      setOpenMenu(null);
                      onCoachChange(item);
                    }}
                  >
                    <CoachAvatar
                      coach={item}
                      className={styles.row_avatar}
                      fontSize={18}
                    />
                    <div className={styles.row_details}>
                      <div className={styles.option_title}>{item.fullName}</div>
                      {itemMeta !== "" && (
                        <div className={styles.row_description}>{itemMeta}</div>
                      )}
                      {/* Figma #39 asked whether this dropdown carries "only names
                          or something more sophisticated"; the answer is the coach
                          card's anatomy — name, title, and how many session types
                          they offer — so choosing a coach here reads the same as
                          choosing one from the grid. */}
                      <div className={styles.row_sessions}>
                        <i className="ico ico-calendar" />
                        <span>
                          {t("{{count}} session types", {
                            count: sessionTypeCount(item),
                          })}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {option && (
        <div className={styles.card}>
          <div className={styles.duration}>
            <span className={styles.duration_value}>
              {option.durationInMinutes}
            </span>
            <span className={styles.duration_unit}>{t("MIN")}</span>
          </div>
          <div className={styles.details}>
            <div className={styles.name_row}>
              <span className={styles.option_title}>{option.title}</span>
              {canSwitchOption && (
                <button
                  type="button"
                  className={cls(styles.chevron_button, {
                    [styles.chevron_button_open]: openMenu === "option",
                  })}
                  onClick={() =>
                    setOpenMenu((open) => (open === "option" ? null : "option"))
                  }
                  aria-label={t("Session")}
                  aria-expanded={openMenu === "option"}
                  data-testid="booking-option-switch"
                >
                  <i className="ico ico-chevron" />
                </button>
              )}
            </div>
            <div className={styles.meta}>{optionDescription}</div>
            {statusLine(option)}

            {openMenu === "option" && (
              // The frame lists options the way the card above states the chosen
              // one — badge, title, what it is, what it costs — so the menu reads
              // as the same thing repeated rather than a list of names.
              <div className={styles.menu}>
                {options.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={styles.option_row}
                    onClick={() => {
                      setOpenMenu(null);
                      onOptionChange(item);
                    }}
                  >
                    <div className={styles.duration}>
                      <span className={styles.duration_value}>
                        {item.durationInMinutes}
                      </span>
                      <span className={styles.duration_unit}>{t("MIN")}</span>
                    </div>
                    <div className={styles.row_details}>
                      <div className={styles.option_title}>{item.title}</div>
                      <div className={styles.row_description}>
                        {describe(item)}
                      </div>
                      {statusLine(item)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCards;
